import React, { Component } from 'react'
import { Mutation, Query } from 'react-apollo'
import { Link, withRouter } from 'react-router-dom'
import map from 'lodash/map'
import pick from 'lodash/pick'
import get from 'lodash/get'
import { toast } from 'react-toastify'
import { createEditorState } from 'medium-draft'
import { convertToRaw } from 'draft-js'
import mediumDraftExporter from 'medium-draft/lib/exporter'
import mediumDraftImporter from 'medium-draft/lib/importer'

import { getImageDimensions, isValidImageSize, isValidUri } from 'utils/helpers'
import { CONTENT_CATEGORIES, contentTypeOptions } from 'config/constants'
import {
  CONTENTS_QUERY,
  CREATE_CONTENT_MUTATION,
  TAGS_QUERY,
  UPDATE_CONTENT_MUTATION,
} from 'graphql/queries'

import {
  Button,
  Dropdown,
  Form,
  Grid,
  Header,
  Segment,
} from 'semantic-ui-react'
import MediumEditor from './MediumDraft'
import { parseYoutubeId } from 'utils/youtubeUrl'

const WEB_HOST = process.env.WEB_HOST

const SourceTable = ({ sources, deleteSource }) => {
  return (
    <table className="bordered">
      <thead>
        <tr>
          <th>Source Name</th>
          <th>Source URL</th>
        </tr>
      </thead>
      <tbody>
        {sources.map((source, sourceNumber) => (
          <tr key={source.name}>
            <td>{source.name}</td>
            <td>
              <a target="_blank" rel="noopener noreferrer" href={source.url}>
                {source.url}
              </a>
            </td>
            <td style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn red lighten-1"
                onClick={() => deleteSource(source.name)}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function convertStateToHtml(state) {
  return mediumDraftExporter(state.getCurrentContent())
}

function getPlainText(state) {
  const content = state.getCurrentContent()
  if (content && content.getPlainText) {
    const plainText = content.getPlainText() || ''
    return plainText.replace(/\n/g, ' ')
  }
  return ''
}

const HTML_DECODE = {
  "&lt;"  : "<",
  "&gt;"  : ">",
  "&amp;" : "&",
  "&nbsp;": " ",
  "&quot;": "\""
};

function decodeHtml(s){
  return (typeof s != "string") ? s :
      s.replace( /&\w+;|&#(\d+);/g,
          function($0,$1){
            var c = HTML_DECODE[$0];
            if(c === undefined){
              // Maybe is Entity Number
              if(!isNaN($1)){
                c = String.fromCharCode(($1 == 160) ? 32 : $1);
              }else{
                // Not Entity Number
                c = $0;
              }
            }
            return c;
          });
};

function convertHtmlToState(html) {
  if (!html) return ''
  return createEditorState(convertToRaw(mediumDraftImporter(decodeHtml(html))))
}

function bodyToHTML(body) {
  if (!body) return ''
  return map(body, body => {
    let htmlBody = ''
    if (body.header) {
      htmlBody += `<h1>${body.header}</h1>`
    }

    if (body.imgUrl) {
      htmlBody += `<figure class="md-block-image"><img src="${
        body.imgUrl
      }"></figure>`
    }

    if (body.text) {
      const textWithFigures = body.text
        .split('><')
        .map(tag => {
          if (tag.includes('img src')) {
            return `figure class="md-block-image"><${tag}></figure`
          }
          return tag
        })
        .join('><')
      htmlBody += textWithFigures
    }

    return htmlBody
  }).join('')
}

function mapContentTypeByTemplate(template) {
  if (!template) return template

  switch (template) {
    case 'card':
    case 'video':
    case 'infographic':
    case 'news':
      return template
    default:
      return 'normal'
  }
}

function pickContentVariables(state) {
  return {
    input: {
      tags: map(state.selectedTags, tag => pick(tag, ['id', 'name'])),
      editorState: convertStateToHtml(state.editorState),
      plainText: getPlainText(state.editorState),
      title: state.title,
      imgUrl: state.imgUrl,
      description: state.description,
      category: state.category,
      template: state.template,
      contentType: mapContentTypeByTemplate(state.template),
      videoUrl: state.videoUrl,
      sources: state.sources,
    },
    files: state.files,
  }
}

class FormCommons extends Component {
  state = {
    template: '',
    title: '',
    description: '',
    category: '',
    imgUrl: '',
    tags: [],
    sources: [],
    editorState: createEditorState(),
    publishedTime: null,

    selectedTags: [],
    topImage: null,
    searchQuery: '',
    sourceName: '',
    sourceUrl: '',
    sourceValid: true,
  }

  transformContentPropsToState(values) {
    const { body, tags, sources, editorState, ...rest } = values
    let state = ''

    if (editorState) {
      state = convertHtmlToState(editorState)
    } else if (body) {
      state = createEditorState(
        convertToRaw(mediumDraftImporter(bodyToHTML(body))),
      )
    }

    return {
      ...rest,
      tags: tags && tags.map(tag => tag.id),
      sources: sources && sources.map(({ name, url }) => ({ name, url })),
      selectedTags: tags,
      editorState: state,
    }
  }

  componentDidMount() {
    if (this.props.initialValues) {
      this.setState(this.transformContentPropsToState(this.props.initialValues))
    }
  }

  handleCategoryChange = (_, { value }) => {
    this.setState({ category: value })
  }

  handleImageFileChange = async event => {
    const input = event.target
    const preview = document.querySelector(`#${event.target.name}-preview`)
    const [f] = input.files
    if (!f) {
      return toast.error('Unable to load image')
    }

    this.setState({ filesLoading: true })
    const dimensions = await getImageDimensions(f)
    if (!isValidImageSize(dimensions)) {
      this.setState({ filesLoading: false })
      return toast.error('Invalid image size. Must be at least 600px by 300px')
    }

    this.setState({ files: f, filesLoading: true })
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      preview.src = reader.result
      this.setState({ filesLoading: false })
    })

    reader.readAsDataURL(f)
  }

  handleTagChange = (event, { value, options }) => {
    this.setState({
      tags: value,
      selectedTags: value.map(id => {
        const tag = options.find(o => o.value === id)
        return {
          id: tag.value,
          name: tag.name,
        }
      }),
      searchQuery: '',
    })
  }

  handleSearchChange = (event, { searchQuery }) => {
    this.setState({ searchQuery })
  }

  handleSearchFilter = options => options
  handleAddSource = event => {
    this.setState(state => {
      return {
        sources: state.sources.concat({
          name: state.sourceName,
          url: state.sourceUrl,
        }),
        sourceName: '',
        sourceUrl: '',
      }
    })
  }

  handleDeleteSource = name => {
    this.setState(state => {
      return {
        sources: state.sources.filter(source => source.name !== name),
      }
    })
  }

  handleChange = (event, data) => {
    if (data.type === 'url') {
      const sourceValid = isValidUri(data.value)
      this.setState({
        [event.target.id]: event.target.value,
        sourceValid,
      })
    } else {
      this.setState({ [event.target.id]: event.target.value })
    }
  }

  handleEditorChange = editorState => {
    this.setState({ editorState })
  }

  handleTemplateChange = (event, { value }) => {
    this.setState({ template: value, contentType: value })
  }

  renderSaveNewButton() {
    return (
      <Mutation
        mutation={CREATE_CONTENT_MUTATION}
        refetchQueries={[
          {
            query: CONTENTS_QUERY,
            variables: {
              offset: 0,
              limit: 20,
              q: '',
            },
          },
        ]}
      >
        {(createContent, { loading }) => {
          return (
            <Button
              primary
              loading={loading}
              onClick={async () => {
                return createContent({
                  variables: pickContentVariables(this.state),
                }).then(response => {
                  const content = get(response, 'data.createContent')
                  this.props.history.push(`/contents/edit/${content.id}`)
                })
              }}
            >
              {loading ? 'Creating...' : 'Create'}
            </Button>
          )
        }}
      </Mutation>
    )
  }

  renderSaveEditButton() {
    return (
      <Mutation
        mutation={UPDATE_CONTENT_MUTATION}
        refetchQueries={[
          {
            query: CONTENTS_QUERY,
            variables: {
              offset: 0,
              limit: 20,
              q: '',
            },
          },
        ]}
      >
        {(updateContent, { loading }) => {
          return (
            <Button
              primary
              loading={loading}
              onClick={async () => {
                const variables = {
                  id: this.props.match.params.contentId,
                  ...pickContentVariables(this.state),
                }
                const response = await updateContent({ variables })
                this.setState(
                  this.transformContentPropsToState(
                    get(response, 'data.updateContent'),
                  ),
                )
              }}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
          )
        }}
      </Mutation>
    )
  }

  renderPublishButton() {
    const { id, publishedTime } = this.state
    return (
      <Mutation mutation={UPDATE_CONTENT_MUTATION}>
        {(updateContent, { loading }) => {
          if (!id) {
            return (
              <Button disabled color="grey">
                Save to Publish
              </Button>
            )
          }
          return (
            <Button
              color={publishedTime ? 'orange' : 'green'}
              disabled={loading}
              onClick={() => {
                const confirm = window.confirm(
                  `Are you sure you want to ${
                    publishedTime ? 'un' : ''
                  }publish this article?`,
                )

                if (!confirm) return

                updateContent({
                  variables: {
                    id,
                    input: { publishedTime: publishedTime ? null : Date.now() },
                  },
                }).then(response => {
                  const content = get(response, 'data.updateContent')
                  console.log(`content`, content)
                  return this.setState(state =>
                    this.transformContentPropsToState(content),
                  )
                })
              }}
            >
              {publishedTime ? 'Unpublish' : 'Publish'}
              {loading ? 'ing' : ''}
            </Button>
          )
        }}
      </Mutation>
    )
  }

  render() {
    const { match } = this.props
    const {
      id,
      // content fields
      title,
      description,
      category,
      template,
      sources,
      videoUrl,
      imgUrl,
      contentType,

      // helper fields
      tags,
      searchQuery,
      sourceName,
      sourceUrl,
      sourceValid,
      publishedTime,
    } = this.state

    const hasTopImage =
      template === 'article' ||
      template === 'listicle' ||
      template === 'news' ||
      template === 'infographic'
    const hasTopVideo = contentType === 'video'
    const hasDescription = template === 'card' || template === 'news'
    const hasSources = contentType !== 'video'
    const disableFormType = match && match.params && match.params.contentId
    const youtubeId = parseYoutubeId(videoUrl)

    return (
      <React.Fragment>
        <div style={{ marginBottom: 12 }}>
          <Grid columns={2}>
            <Grid.Row>
              <Grid.Column>
                <Dropdown
                  placeholder="Select content type..."
                  options={contentTypeOptions}
                  value={this.state.template}
                  onChange={this.handleTemplateChange}
                  disabled={!!disableFormType}
                  selection
                />
              </Grid.Column>

              <Grid.Column textAlign="right">
                <div
                  style={{
                    position: 'absolute',
                    right: '14px',
                    bottom: '55px',
                  }}
                >
                  Preview:{' '}
                  {publishedTime ? (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${WEB_HOST}/article/${id}`.replace('cms', 'ctt')}
                    >
                      {title}
                    </a>
                  ) : (
                    <span title="Unpublished article">Unavailable</span>
                  )}
                </div>
                <div>
                  {id
                    ? this.renderSaveEditButton()
                    : this.renderSaveNewButton()}

                  {this.renderPublishButton()}

                  <Link to="/contents">
                    <Button color="teal">Back to Articles</Button>
                  </Link>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>

        {!template ? (
          <Header as="h1" content="Select a content type..." />
        ) : (
          <Form>
            <div className="details-section" style={{ display: 'flex' }}>
              <div className="left" style={{ flex: 4 }}>
                <Form.Group>
                  <Form.Select
                    width={16}
                    required
                    label="Category"
                    placeholder="Select category..."
                    value={category}
                    options={CONTENT_CATEGORIES}
                    onChange={this.handleCategoryChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Input
                    required
                    width={16}
                    id="title"
                    placeholder="Title"
                    label="Title"
                    onChange={this.handleChange}
                    value={title}
                  />
                </Form.Group>
                {hasDescription && (
                  <Form.Group>
                    <Form.TextArea
                      width={16}
                      id="description"
                      label="Description"
                      placeholder="Description"
                      onChange={this.handleChange}
                      value={description}
                    />
                  </Form.Group>
                )}
                <Form.Group>
                  <Query
                    query={TAGS_QUERY}
                    // will show all tags at once in the results;
                    // a hacky way to query all tags and use apollo client cache
                    // TODO: refactor and implement a better fuzzy search
                    variables={{ q: searchQuery, limit: 100 }}
                    context={{ debounceKey: 'tags', debounceTimeout: 100 }}
                  >
                    {({ data, loading }) => {
                      const selectedTags = map(this.state.selectedTags, t => ({
                        text: t.name,
                        name: t.name,
                        value: t.id,
                      }))

                      const options = map(data.tags, ({ name, id }) => ({
                        text: name,
                        name,
                        value: id,
                      })).concat(selectedTags)

                      return (
                        <Form.Select
                          width={16}
                          required
                          label="Tags"
                          name="Select tags..."
                          multiple
                          search={this.handleSearchFilter}
                          loading={loading}
                          selection
                          value={tags}
                          options={options}
                          searchQuery={searchQuery}
                          onChange={this.handleTagChange}
                          onSearchChange={this.handleSearchChange}
                        />
                      )
                    }}
                  </Query>
                </Form.Group>
                {hasSources && (
                  <Form.Group>
                    <Form.Input
                      width={6}
                      id="sourceName"
                      placeholder="Source name"
                      label="Source name"
                      value={sourceName}
                      onChange={this.handleChange}
                    />
                    <Form.Input
                      width={6}
                      id="sourceUrl"
                      placeholder="Source URL"
                      label="Source URL"
                      type="url"
                      value={sourceUrl}
                      onChange={this.handleChange}
                      error={!sourceValid}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        marginBottom: '1px',
                      }}
                    >
                      <Button
                        width={4}
                        positive
                        content="Add Source"
                        onClick={this.handleAddSource}
                        disabled={!sourceValid}
                      />
                    </div>
                  </Form.Group>
                )}

                {hasSources ? (
                  <div className="row">
                    {sources.length ? (
                      <SourceTable
                        deleteSource={this.handleDeleteSource}
                        sources={sources}
                      />
                    ) : null}
                  </div>
                ) : null}
                {/* end left */}
              </div>

              {/* right */}
              {(hasTopImage || hasTopVideo) && (
                <div
                  className="right"
                  style={{ flex: 3, margin: '24px 0 0 40px' }}
                >
                  {hasTopImage && (
                    <React.Fragment>
                      <Form.Group>
                        <Form.Input
                          label={`${
                            template === 'infographic' ? 'Thumbnail' : 'Main'
                          } Image Upload`}
                          name="imgUpload"
                          type="file"
                          loading={this.state.filesLoading}
                          disabled={this.state.filesLoading}
                          accept=".jpg,.png,.jpeg,.gif,.svg"
                          onChange={this.handleImageFileChange}
                        />
                      </Form.Group>
                      <div>
                        <img
                          width="100%"
                          src={imgUrl || ''}
                          alt=""
                          className="responsive-img"
                          id="imgUpload-preview"
                        />
                      </div>
                    </React.Fragment>
                  )}

                  {hasTopVideo && (
                    <Form.Group>
                      <Form.Input
                        width={16}
                        id="videoUrl"
                        label="Video URL"
                        type="url"
                        required
                        placeholder="https://www.youtube.com/embed/tRcPA7Fzebw"
                        value={videoUrl}
                        onChange={this.handleChange}
                      />
                    </Form.Group>
                  )}
                  {youtubeId && (
                    <div
                      className="video"
                      style={{
                        position: 'relative',
                        paddingBottom: '56.25%' /* 16:9 */,
                        paddingTop: 25,
                        height: 0,
                      }}
                    >
                      <iframe
                        title="Youtube Preview"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                        src={`https://www.youtube.com/embed/${youtubeId}`}
                        frameBorder="0"
                      />
                    </div>
                  )}
                </div>
              )}
              {/* end details-section */}
            </div>

            <Header as="h5" content="Content" />
            <div style={{ marginBottom: '50px' }}>
              <Segment>
                <MediumEditor
                  editorState={this.state.editorState}
                  handleEditorChange={this.handleEditorChange}
                />
              </Segment>
            </div>
          </Form>
        )}
      </React.Fragment>
    )
  }
}

export default withRouter(FormCommons)
